import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { ContextMenuItem } from './ContextMenuItem';
import { ContextMenuSeparator } from './ContextMenuSeparator';
export const ContextMenu = ({ items, position, onClose, isOpen }) => {
    var _a, _b;
    // Wrapper ref groups the primary menu + active submenu so click-outside
    // detection treats both as "inside". Primary-menu ref is only used to
    // measure size for viewport clamping — must be on the actual styled div,
    // not the wrapper, since the wrapper has no box and would report
    // viewport-wide dimensions.
    const menuRef = useRef(null);
    const primaryMenuRef = useRef(null);
    const [openSubmenu, setOpenSubmenu] = useState(null);
    const handleClickOutside = useCallback((event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            onClose();
        }
    }, [onClose]);
    const preventDefaultContextMenu = useCallback((event) => {
        event.preventDefault();
        onClose();
    }, [onClose]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside, true);
            document.addEventListener('click', handleClickOutside, true);
            document.addEventListener('contextmenu', preventDefaultContextMenu, true);
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
                document.removeEventListener('click', handleClickOutside, true);
                document.removeEventListener('contextmenu', preventDefaultContextMenu, true);
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isOpen, handleClickOutside, preventDefaultContextMenu, handleKeyDown]);
    // Reset submenu state whenever the menu opens fresh.
    useEffect(() => {
        if (!isOpen)
            setOpenSubmenu(null);
    }, [isOpen]);
    const getAdjustedPosition = useCallback(() => {
        if (!primaryMenuRef.current)
            return position;
        const menuRect = primaryMenuRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        let { x, y } = position;
        if (x + menuRect.width > viewportWidth) {
            x = Math.max(0, viewportWidth - menuRect.width - 10);
        }
        if (y + menuRect.height > viewportHeight) {
            y = Math.max(0, viewportHeight - menuRect.height - 10);
        }
        return { x, y };
    }, [position]);
    const [adjustedPosition, setAdjustedPosition] = useState(position);
    useEffect(() => {
        if (isOpen && primaryMenuRef.current) {
            setAdjustedPosition(getAdjustedPosition());
        }
    }, [isOpen, getAdjustedPosition]);
    const handleSubmenuTriggerEnter = useCallback((item, e) => {
        if (!item.submenu || item.submenu.length === 0) {
            setOpenSubmenu(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        // Position the submenu to the right of the trigger row, top-aligned with
        // it. Flip to the left if it would overflow the viewport — same logic
        // as native context menu submenus.
        const SUBMENU_WIDTH = 220;
        let left = rect.right + 2;
        if (left + SUBMENU_WIDTH > window.innerWidth) {
            left = Math.max(0, rect.left - SUBMENU_WIDTH - 2);
        }
        setOpenSubmenu({ id: item.id, left, top: rect.top });
    }, []);
    if (!isOpen) {
        return null;
    }
    // Find the active submenu's items so we can render them in the portal.
    const activeSubmenuItems = openSubmenu
        ? ((_b = (_a = items.find(i => i.id === openSubmenu.id)) === null || _a === void 0 ? void 0 : _a.submenu) !== null && _b !== void 0 ? _b : [])
        : [];
    const menuContent = (_jsxs("div", { ref: menuRef, children: [_jsx("div", { ref: primaryMenuRef, className: "fixed z-[9999] min-w-48 py-1 rounded-md shadow-lg border backdrop-blur-sm", style: {
                    left: `${adjustedPosition.x}px`,
                    top: `${adjustedPosition.y}px`,
                    backgroundColor: 'var(--forest)',
                    borderColor: 'var(--pewter)',
                    maxWidth: '240px'
                }, children: items.map((item, index) => {
                    const hasSubmenu = !!(item.submenu && item.submenu.length > 0);
                    return (_jsxs(React.Fragment, { children: [item.separator && index > 0 && _jsx(ContextMenuSeparator, {}), _jsx("div", { onMouseEnter: (e) => handleSubmenuTriggerEnter(item, e), style: { position: 'relative' }, children: _jsx(ContextMenuItem, { item: item, onItemClick: () => { }, onClose: onClose, hasSubmenu: hasSubmenu }) })] }, item.id));
                }) }), openSubmenu && activeSubmenuItems.length > 0 && (_jsx("div", { className: "fixed z-[10000] min-w-48 py-1 rounded-md shadow-lg border backdrop-blur-sm", style: {
                    left: `${openSubmenu.left}px`,
                    top: `${openSubmenu.top}px`,
                    backgroundColor: 'var(--forest)',
                    borderColor: 'var(--pewter)',
                    maxWidth: '240px'
                }, onMouseLeave: () => setOpenSubmenu(null), children: activeSubmenuItems.map((sub, idx) => (_jsxs(React.Fragment, { children: [sub.separator && idx > 0 && _jsx(ContextMenuSeparator, {}), _jsx(ContextMenuItem, { item: sub, onItemClick: () => { }, onClose: onClose })] }, sub.id))) }))] }));
    return createPortal(menuContent, document.body);
};
